import React, { useCallback, useEffect, useRef, useState } from "react";
import { ConnectBody, ConnectFoot, ConnectScreen, PrimaryButton, SecondaryButton } from "./connect/ConnectScreen";
import CountryPicker from "./connect/CountryPicker";
import { MasspayRail } from "./connect/RailScreens";
import { Middle, MpHold, MpName, MpRetry, MpWaiting, Orb } from "./MasspayFaces";
import MasspayFeeTable, { SMALL_CASHOUT_USD, SmallCashoutLine } from "./MasspayFeeTable";
import { CheckIcon } from "./sheetIcons";
import { fansApi } from "../services/fans";
import type { FanMoney } from "../types/fans";
import { fetchCountryData, SupportedCountry } from "../lib/supportedCountries";
import { onIdCheckReturn, openIdCheck, reserveIdCheckTab } from "../lib/idCheckFlow";
import { openMasspayPage } from "../lib/masspayWallet";
import { formatAmountForCopy, formatTxnAmount } from "../utils/currency";
import { t } from "../i18n";
import "./FanCashoutFlow.css";

// Fan cash-out (Fan Refer & Earn part 2, founder 10/29 — the screens on
// client/.claude/fan-cashout-screens.html). The talent's 1.5.2 full-screen
// MassPay steps, word for word where they exist:
//
//   first time   country → MassPay card → name as on ID → ID check → confirm → sent
//   after that   confirm → sent
//
// No founder approval after the fan's ID check (founder pick 2): MassPay's
// "approved" is the end of the set-up. Money is always US dollars. Every step
// has a way forward (CLAUDE.md "cover every path"): try again, contact
// support, or close — the money stays safe on the server either way.

type Face = "loading" | "country" | "rail" | "name" | "waiting" | "checking" | "retry" | "hold" | "confirm" | "confirming" | "sent";

// A brand name, never translated (the talent screens do the same: connect/RailScreens BRAND).
const MASSPAY = "MassPay";
const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M} .'-]{0,49}$/u;
const newKey = () => `fco_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
const usd = (n: number) => formatTxnAmount(n, "usd");

/** "Venmo, PayPal or your bank" — the server's list, "bank" last (lib/fanCashout.js withdrawTo). */
export function withdrawLine(list: string[] | undefined): string {
  const names = (list && list.length ? list : ["bank"]).map((w) => (w === "bank" ? t("fanCashout.yourBank") : w));
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} ${t("fanCashout.or")} ${names[names.length - 1]}`;
}

/** The fan's first face: where their MassPay set-up stands. */
export function startFace(money: FanMoney): Face {
  const p = money.payout;
  if (!p) return "country";
  if (p.onHold) return "hold";
  switch (p.kycStatus) {
    case "approved":
      return "confirm";
    case "review":
      return "checking";
    case "declined":
    case "resubmission":
      return "retry";
    default:
      return "name"; // payee made, ID check not finished: the same session comes back
  }
}

/** What the MassPay card names for a country (Venmo is US only; PayPal only after the founder's tests). */
export function walletsForFan(country: SupportedCountry, venmoPaypal: boolean): string[] {
  if (country.code === "US") return venmoPaypal ? ["Venmo", "PayPal"] : [];
  if (country.wallets.length) return country.wallets;
  return venmoPaypal ? ["PayPal"] : [];
}

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12h13" /><path d="M13 6l6 6-6 6" /></svg>
);
const IdIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" /><circle cx="9" cy="11" r="2.2" /><path d="M5.8 16.2c.7-1.6 1.8-2.4 3.2-2.4s2.5.8 3.2 2.4" /><path d="M14.5 10h4M14.5 13.5h3" />
  </svg>
);

export default function FanCashoutFlow(props: {
  money: FanMoney;
  onClose: () => void;
  /** Money moved or the set-up changed: the page re-reads its numbers. */
  onChanged: () => void;
}) {
  const { money, onClose, onChanged } = props;
  const [face, setFace] = useState<Face>(() => startFace(money));
  const [countries, setCountries] = useState<SupportedCountry[] | null>(null);
  const [detected, setDetected] = useState<string | null>(null);
  const [picked, setPicked] = useState<SupportedCountry | null>(null);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [support, setSupport] = useState(false);
  const [note, setNote] = useState("");
  const [sent, setSent] = useState<number | null>(null);
  const [uploadUrl, setUploadUrl] = useState<string | null>(null);
  const keyRef = useRef(newKey());
  const stopReturnRef = useRef<() => void>(() => {});
  const ready = money.ready || 0;

  useEffect(() => () => stopReturnRef.current(), []);

  // The country list (fans can cash out anywhere MassPay pays: every open country).
  useEffect(() => {
    if (face !== "country" || countries) return;
    let alive = true;
    fetchCountryData({ fresh: true }).then((d) => {
      if (!alive) return;
      setCountries(d.countries.filter((c) => c.open));
      setDetected(d.detectedCode);
    });
    return () => {
      alive = false;
    };
  }, [face, countries]);

  const go = (f: Face) => {
    setError("");
    setSupport(false);
    setFace(f);
  };

  // Back from MassPay's ID check (or "I'm done"): ask MassPay where it stands.
  const checkStatus = useCallback(async () => {
    setPending(true);
    setNote("");
    try {
      const s = await fansApi.cashoutStatus();
      onChanged();
      if (s.onHold) return go("hold");
      if (s.kycStatus === "approved") return go("confirm");
      if (s.kycStatus === "review") return go("checking");
      if (s.kycStatus === "declined" || s.kycStatus === "resubmission") return go("retry");
      setNote(t("fanCashout.notFinishedYet"));
    } catch {
      setError(t("connectBankSheet.couldnTReachMasspay"));
    } finally {
      setPending(false);
    }
  }, [onChanged]);

  const startIdCheck = async () => {
    const legalFirstName = first.trim();
    const legalLastName = last.trim();
    if (!NAME_RE.test(legalFirstName) || !NAME_RE.test(legalLastName)) return setError(t("connectBankSheet.enterYourName"));
    setPending(true);
    setError("");
    setSupport(false);
    const tab = reserveIdCheckTab();
    try {
      const ans = await fansApi.cashoutConnect({ legalFirstName, legalLastName, countryCode: picked?.code || money.payout?.country || "" });
      if (ans?.done) {
        tab?.close();
        setPending(false);
        return go("confirm");
      }
      if (!ans?.url) throw new Error("no_url");
      stopReturnRef.current();
      const stop = onIdCheckReturn(() => {
        stop();
        stopReturnRef.current = () => {};
        checkStatus();
      });
      stopReturnRef.current = stop;
      await openIdCheck(ans.url, tab);
      setPending(false);
      onChanged();
      setFace("waiting");
    } catch (e: any) {
      tab?.close();
      setPending(false);
      const code = e?.response?.data?.code;
      if (code === "id_check_limit") {
        setError(t("connectBankSheet.youVeUsedYourId"));
        setSupport(true);
      } else if (code === "id_checks_paused") setError(t("connectBankSheet.idChecksArePaused"));
      else if (code === "email_needed") setError(t("fanCashout.addAnEmail"));
      else if (code === "country_not_open") setError(t("fanCashout.notOpenThere"));
      else if (e?.response?.status === 400) setError(t("connectBankSheet.enterYourName"));
      else setError(t("connectBankSheet.couldnTReachMasspay"));
    }
  };

  const cashOut = async () => {
    setPending(true);
    setError("");
    try {
      const r = await fansApi.cashout(keyRef.current);
      onChanged();
      // 202 "we're confirming with MassPay" is a 2xx too: only `sent` is sent.
      if (!r?.sent) return go("confirming");
      setSent(r.amount ?? ready);
      go("sent");
    } catch (e: any) {
      const status = e?.response?.status;
      const reason = e?.response?.data?.reason;
      // No answer at all: the same key again is safe (never a second cash-out).
      if (!e?.response) setError(t("fanCashout.couldnTReach"));
      else if (status === 202 || reason === "masspay_unconfirmed") go("confirming"); // older axios setups
      else if (reason === "payouts_paused") setError(t("useCashout.masspayPaused"));
      else if (reason === "masspay_documents_needed") {
        setUploadUrl(e.response.data.uploadUrl || null);
        setError(t("serverMsg.masspayNeedsADocumentFromYou"));
      } else if (reason === "id_check_needed") go("name");
      else if (reason === "masspay_on_hold") go("hold");
      else {
        keyRef.current = newKey(); // a real refusal: the next tap is a new cash-out
        setError(reason === "below_minimum" ? t("fanCashout.startsAt", { amount: formatAmountForCopy(money.minimum || 20, "usd") }) : t("useCashout.masspayRefused"));
        onChanged();
      }
    } finally {
      setPending(false);
    }
  };

  const progress = { loading: 10, country: 20, rail: 40, name: 60, waiting: 75, checking: 85, retry: 60, hold: 60, confirm: 90, confirming: 100, sent: 100 }[face];
  const back =
    face === "rail" ? () => go("country") : face === "name" && picked ? () => go("rail") : undefined;

  let body: React.ReactNode = null;
  if (face === "country") {
    body = countries ? (
      <CountryPicker
        countries={countries}
        selectedCode={picked?.code || detected}
        pending={pending}
        onPick={(c) => {
          setPicked(c);
          go("rail");
        }}
      />
    ) : (
      <ConnectBody center><div className="fco-loading" aria-hidden="true" /></ConnectBody>
    );
  } else if (face === "rail" && picked) {
    body = <MasspayRail country={{ name: picked.name, code: picked.code, wallets: walletsForFan(picked, !!money.venmoPaypal) }} onChange={() => go("country")} onContinue={() => go("name")} />;
  } else if (face === "name") {
    body = <MpName first={first} last={last} onFirst={setFirst} onLast={setLast} pending={pending} error={error} support={support} onSubmit={startIdCheck} />;
  } else if (face === "waiting") {
    body = <MpWaiting pending={pending} note={note} error={error} onDone={checkStatus} onReopen={startIdCheck} />;
  } else if (face === "checking") {
    body = (
      <>
        <Middle orb={<Orb><IdIcon /></Orb>} title={t("fanCashout.checkingYourId")} sub={t("fanCashout.usuallyAFewMinutes")} />
        <ConnectFoot>
          <PrimaryButton onClick={checkStatus} disabled={pending} busy={pending}>{t("fanCashout.checkAgain")}</PrimaryButton>
          <SecondaryButton onClick={onClose}>{t("connectBankSheet.close")}</SecondaryButton>
        </ConnectFoot>
      </>
    );
  } else if (face === "retry") {
    body = <MpRetry country={picked?.name || ""} canChangeCountry={false} onRetry={() => go("name")} onChangeCountry={() => go("country")} />;
  } else if (face === "hold") {
    body = <MpHold onClose={onClose} />;
  } else if (face === "confirm") {
    body = (
      <>
        <ConnectBody center>
          <p className="fco-k">{t("fanCashout.cashOut")}</p>
          <p className="fco-amt">{usd(ready)}</p>
          <p className="cx-sub">{t("fanCashout.toYourMasspayWallet")}</p>
          <div className="cx-mp-card fco-card">
            <img className="cx-mp-wm" src="/masspay-wordmark-white.svg" alt={MASSPAY} />
            <p className="cx-mp-label">{t("fanCashout.thenWithdrawTo")}</p>
            {/* MassPay's own fees + minimums for this amount (founder 10/29);
                no answer from MassPay → the plain chips, as before. */}
            {money.withdrawFees?.options?.length ? (
              <MasspayFeeTable fees={money.withdrawFees} />
            ) : (
              <div className="cx-chips">
                {(money.withdrawTo || ["bank"]).map((w) =>
                  w === "bank" ? <span className="cx-chip cx-chip-bank" key={w}>{t("connectBank.yourBank")}</span> : <span className="cx-chip" key={w}>{w}</span>
                )}
              </div>
            )}
            <div className="cx-mp-div" />
            <p className="cx-ck">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
              {t("fanCashout.feeShownFirst")}
            </p>
          </div>
          <SmallCashoutLine amount={ready} />
        </ConnectBody>
        <ConnectFoot>
          {error && <p className="cx-error" role="alert">{error}</p>}
          {uploadUrl && <SecondaryButton onClick={() => openMasspayPage(uploadUrl)}>{t("fanCashout.uploadIt")}</SecondaryButton>}
          <PrimaryButton onClick={cashOut} disabled={pending || ready <= 0} busy={pending} noChevron>
            {t("fanCashout.cashOutAmount", { amount: usd(ready) })}
          </PrimaryButton>
          {ready < SMALL_CASHOUT_USD && <SecondaryButton onClick={onClose}>{t("masspayFees.notNow")}</SecondaryButton>}
        </ConnectFoot>
      </>
    );
  } else if (face === "confirming") {
    body = (
      <>
        <Middle orb={<Orb><SendIcon /></Orb>} title={t("fanCashout.confirmingWithMasspay")} sub={t("fanCashout.yourMoneyIsSafe")} />
        <ConnectFoot><PrimaryButton onClick={onClose} noChevron>{t("connectBankSheet.close")}</PrimaryButton></ConnectFoot>
      </>
    );
  } else if (face === "sent") {
    body = (
      <>
        <Middle
          orb={<Orb><CheckIcon /></Orb>}
          title={t("cashoutFlow.sentToYourMasspayWallet")}
          sub={t("fanCashout.isThereNow", { amount: usd(sent ?? ready), list: withdrawLine(money.withdrawTo) })}
        />
        <ConnectFoot>
          <PrimaryButton onClick={() => openMasspayPage(money.walletUrl)}>{t("cashoutFlow.openMasspay")}</PrimaryButton>
          <SecondaryButton onClick={onClose}>{t("fanCashout.done")}</SecondaryButton>
        </ConnectFoot>
      </>
    );
  }

  return (
    <ConnectScreen progress={progress} onClose={onClose} onBack={back} busy={pending}>
      {body}
    </ConnectScreen>
  );
}
